import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCreatorRequests } from '../api/waitlistApi';

/**
 * Number of Creator requests waiting for a god's decision, for the
 * ``Waiting List (N)`` label in the Settings sidebar.
 *
 * There is no count endpoint — ``GET /platform/creator-requests?status=pending``
 * returns the whole (small) queue, so the count is its length. ``count`` is
 * ``null`` until the first response arrives, after a failed fetch, and
 * whenever ``enabled`` is false (non-gods and self-hosted installs never
 * call the god-only route), so callers can fall back to the bare label
 * instead of showing a number they do not have.
 *
 * ``refresh`` re-reads the queue; ``WaitingListManagement`` calls it after a
 * status change so the label follows approvals and rejections without a
 * reload. Responses are applied in request order only: a slow earlier fetch
 * that resolves after a later one is dropped, and nothing is applied after
 * unmount.
 */
export default function usePendingCreatorRequestCount({ enabled }) {
    const [count, setCount] = useState(null);
    const latestRequest = useRef(0);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const refresh = useCallback(async () => {
        if (!enabled) return;
        latestRequest.current += 1;
        const requestId = latestRequest.current;
        let next = null;
        try {
            const rows = await fetchCreatorRequests('pending');
            next = Array.isArray(rows) ? rows.length : null;
        } catch (_) {
            next = null;
        }
        if (mounted.current && requestId === latestRequest.current) {
            setCount(next);
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            // Invalidate in-flight fetches too: a god demoted mid-request must
            // not end up with a number on a tab they no longer have.
            latestRequest.current += 1;
            setCount(null);
            return;
        }
        refresh();
    }, [enabled, refresh]);

    return { count, refresh };
}
